<template>
  <v-dialog
    :value="value"
    @input="(v) => v || close()"
    hide-overlay
    persistent
    no-click-animation
    fullscreen
  >
    <v-card>
      <v-card-title class="title pa-0">
        <v-row justify="center" class="show-image">
          <v-col cols="auto" align-self="center">
            <v-btn
              fab
              small
              outlined
              @click="previousImage(image.index)"
            >
              <v-icon small> mdi-less-than </v-icon>
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <h5>({{ image.index + 1 }}/{{ length }}) {{ image.name }}</h5>
          </v-col>
          <v-col cols="auto" align-self="center">
            <v-btn
              fab
              small
              outlined
              @click="nextImage(image.index)"
            >
              <v-icon small> mdi-greater-than </v-icon>
            </v-btn>
          </v-col>
          <v-col cols="auto" align-self="center" v-if="editable">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    v-bind="attrs"
                    v-on="on"
                    fab
                    small
                    outlined
                    @click="rotateImage"
                >
                  <v-icon small> mdi-crop-rotate </v-icon>
                </v-btn>
              </template>
              <span>画像回転</span>
            </v-tooltip>
          </v-col>
          <v-col cols="auto" align-self="center" v-if="editable">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    v-bind="attrs"
                    v-on="on"
                    fab
                    small
                    outlined
                    @click="startCrop"
                    :color="isCrop ? 'primary' : 'default'"
                >
                  <v-icon small> mdi-crop-free </v-icon>
                </v-btn>
              </template>
              <span>画像クロップ</span>
            </v-tooltip>
          </v-col>
          <v-col cols="auto" align-self="center" v-if="editable">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    v-bind="attrs"
                    v-on="on"
                    fab
                    small
                    outlined
                    @click="markAnnotation"
                    :color="isMarkAnnotation ? 'primary' : 'default'"
                >
                  <v-icon small> mdi-map-marker-plus-outline </v-icon>
                </v-btn>
              </template>
              <span>アノテーション付き</span>
            </v-tooltip>
          </v-col>
          <v-col cols="auto" align-self="center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    v-bind="attrs"
                    v-on="on"
                    fab
                    small
                    outlined
                    @click="downloadImage(image.src)"
                >
                  <v-icon small> mdi-download </v-icon>
                </v-btn>
              </template>
              <span>画像ダウンロード</span>
            </v-tooltip>
          </v-col>
          <v-col cols="auto" align-self="center" v-if="editable">
            <v-checkbox
                v-model="displayAirprobeAnnotation"
                @change="showAirprobeAnnotation"
                label="自動分析アノテーション"
            >
            </v-checkbox>
          </v-col>
        </v-row>
        <v-btn fab small class="btn-close" depressed color="grey lighten-1" v-on:click="close()">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <div style="height: calc(100vh - 64px)" ref="zoomer" id="zoomer">
        <v-img
            ref="img"
            height="100%"
            lazy-src="@/assets/images/no-image.png"
            class="image"
            max-height="100%"
            aspect-ratio="1"
            v-show="loadingImage">
          <template v-slot:placeholder>
            <v-row
                class="fill-height ma-0"
                align="center"
                justify="center"
            >
              <v-progress-circular
                  :size="70"
                  :width="7"
                  indeterminate
                  color="grey lighten-5"
              ></v-progress-circular>
            </v-row>
          </template>
        </v-img>
        <tui-image-editor
            v-show="!loadingImage"
            :include-ui="useDefaultUI"
            :options="options"
            ref="tuiImageEditor"
            @mousedown="mouseDown"
            @mouseup.native="mouseUp"
            @wheel.native="zoom"
            @objectAdded="objectAdded"
            @objectActivated="objectActivated"
            @objectMoved="objectMoved"
            @undoStackChanged="undo"
            @redoStackChanged="redo"
            @objectScaled="objectScaled"></tui-image-editor>
      </div>
      <div style="height: calc(100vh - 64px)" hidden>
        <tui-image-editor :include-ui="useDefaultUI" :options="options" ref="hidden_tuiImageEditor"></tui-image-editor>
      </div>
    </v-card>
    <crop-image-preview
        v-model="showPreviewDialog"
        v-if="showPreviewDialog"
        :image="cropPreviewImage"
        @saved="draw"
        @turnoff="turnoff"
        :abnormality="abnormality"
        :cropRect="cropRect"
        :cropImage="editCropImage"
        :strokeWidth="strokeWidth"
        :editable="editable"
        :filename="image.name">
    </crop-image-preview>
  </v-dialog>
</template>

<script>
import Vue from "vue";
import VueZoomer from "vue-zoomer";
import {fabric} from 'fabric';
import CropImagePreview from "@/components/dialog/CropImagePreview";
import ImageEditor from '@toast-ui/vue-image-editor/src/ImageEditor.vue';
import {store} from "@/store";
import download from "downloadjs";

Vue.use(VueZoomer);

export default {
  name: "ShowImageDialog",
  props: ["value", "info", 'annotation'],
  components: {
    'crop-image-preview': CropImagePreview,
    'tui-image-editor': ImageEditor
  },
  data() {
    return {
      image: {
        name: null,
        src: null,
        index: null,
        rotate: 0,
        crop: null,
        annotation_mark: null,
        type: null
      },
      length: null,
      rotateAngle: 0,
      isCrop: false,
      showPreviewDialog: false,
      cropPreviewImage: null,
      useDefaultUI: true,
      options: { // for tui-image-editor component's "options" prop
        includeUI: 0,
        usageStatistics: false,
        cssMaxWidth: window.innerWidth,
        cssMaxHeight: window.innerHeight,
        selectionStyle: {
          cornerSize: this.info.type === 'thermal' ? 5: 50,
        },
      },
      isStartCrop: false,
      scale: 1,
      minScale: 1,
      maxScale: 5,

      lastFullWheelTime: 0,
      lastWheelTime: 0,
      lastWheelDirection: 'y',
      pointerPosX: -1,
      pointerPosY: -1,

      currentCenter: {
        x: 0,
        y: 0,
      },
      isMarkAnnotation: false,
      listAnnotation: [],
      listCropImage: [],
      cropRect: null,
      abnormality: [],
      folderId: this.$route.params.folderId,
      editShape: null,
      editCropImage: null,
      editAnnotationShape: null,

      mouseDownPosition: null,

      isStarting: true,
      strokeWidth: 0,
      loadingImage: true,
      strokeDashWidth: this.info.type === 'thermal' ? 20: 60,
      displayAirprobeAnnotation: true,
    };
  },
  methods: {
    reset(){
      this.$refs.tuiImageEditor.invoke('stopDrawingMode');
      this.listAnnotation = [];
      this.rotateAngle = 0;
      this.isCrop = false;
      this.cropPreviewImage = null;
      this.isStartCrop = false;
      this.scale = 1;
      this.lastFullWheelTime = 0;
      this.lastWheelTime = 0;
      this.lastWheelDirection = 'y';
      this.pointerPosX = -1;
      this.pointerPosY = -1;
      this.currentCenter = {
        x: 0,
        y: 0,
      };
      this.isMarkAnnotation = false;
      this.listCropImage = [];
      this.cropRect = null;
      this.abnormality = [];
      this.editShape = null;
      this.editCropImage = null;
      this.editAnnotationShape = null;
      this.mouseDownPosition = null;
      this.isStarting = true;
      this.loadingImage = true;
    },
    resetCanvasSize() {
      const element = document.getElementById("zoomer");
      const canvasWidth = document.getElementsByClassName('lower-canvas')[0].width;
      const canvasHeight = document.getElementsByClassName('lower-canvas')[0].height;
      const height = element.offsetHeight;
      let calc_width = (canvasWidth * height) / canvasHeight;
      if (calc_width > window.innerWidth) {
        calc_width = window.innerWidth;
      }
      element.style.width = calc_width + 'px';
      document.getElementsByClassName('upper-canvas')[0].style.maxWidth = window.innerWidth + 'px';
      document.getElementsByClassName('upper-canvas')[0].style.maxHeight = window.innerHeight + 'px';
      document.getElementsByClassName('lower-canvas')[0].style.maxWidth = window.innerWidth + 'px';
      document.getElementsByClassName('lower-canvas')[0].style.maxHeight = window.innerHeight + 'px';
      document.getElementsByClassName('tui-image-editor-canvas-container')[0].style.maxWidth = window.innerWidth + 'px';
      document.getElementsByClassName('tui-image-editor-canvas-container')[0].style.maxHeight = window.innerHeight + 'px';
    },
    initCanvas(){
      this.setZoomerWidth();
      this.$refs.hidden_tuiImageEditor.invoke('loadImageFromURL', this.image.src.concat('?timestamp=1'), this.image.name).then(() => {
        if (this.image.rotate !== 0){
          this.$refs.hidden_tuiImageEditor.invoke('rotate', this.image.rotate, true);
        }
      });
      this.$refs.hidden_tuiImageEditor.invoke('startDrawingMode', 'CROPPER');
      this.$refs.tuiImageEditor.invoke('loadImageFromURL', this.image.src.concat('?timestamp=1'), this.image.name).then(() => {
        // this.setCanvasDimension();
        document.getElementsByClassName('upper-canvas')[0].style.maxWidth = window.innerWidth + 'px';
        document.getElementsByClassName('upper-canvas')[0].style.maxHeight = window.innerHeight + 'px';
        document.getElementsByClassName('lower-canvas')[0].style.maxWidth = window.innerWidth + 'px';
        document.getElementsByClassName('lower-canvas')[0].style.maxHeight = window.innerHeight + 'px';
        document.getElementsByClassName('tui-image-editor-canvas-container')[0].style.maxWidth = window.innerWidth + 'px';
        document.getElementsByClassName('tui-image-editor-canvas-container')[0].style.maxHeight = window.innerHeight + 'px';

        if (this.image.rotate !== 0){
          this.rotateAngle = this.image.rotate;
          this.$refs.tuiImageEditor.invoke('rotate', this.image.rotate, true).then(() => {
            this.resetCanvasSize();
            this.loadingImage = false;
          });
        }
        else {
          this.loadingImage = false;
        }
        if (!Array.isArray(this.image.annotation_mark)){
          for(const [key, value] of Object.entries(this.image.annotation_mark)){
            if (!this.displayAirprobeAnnotation){
              if ('app' in value){
                if (value.app !== 'airprobe'){
                  setTimeout(() => {
                    this.$refs.tuiImageEditor.invoke('addShape', 'rect', {
                      fill: 'transparent',
                      stroke: value.stroke,
                      strokeWidth: this.strokeWidth,
                      left: value.left,
                      top: value.top,
                      width: value.width,
                      height: value.height,
                      isRegular: false,
                      controls: {
                        ...fabric.Rect.prototype.controls,
                        mtr: new fabric.Control({visible: false})
                      },
                      selectable: this.editable
                    }).then((shape) => {
                      shape['custom_id'] = key;
                      if ('app' in value){
                        shape['app'] = value.app;
                      }
                      shape['visible'] = true;
                      this.listAnnotation.push(shape);
                    });
                  },10);
                }
                else {
                  value['visible'] = this.displayAirprobeAnnotation;
                  value['custom_id'] = key;
                  this.listAnnotation.push(value);
                }
              }
              else {
                setTimeout(() => {
                  this.$refs.tuiImageEditor.invoke('addShape', 'rect', {
                    fill: 'transparent',
                    stroke: value.stroke,
                    strokeWidth: this.strokeWidth,
                    left: value.left,
                    top: value.top,
                    width: value.width,
                    height: value.height,
                    isRegular: false,
                    controls: {
                      ...fabric.Rect.prototype.controls,
                      mtr: new fabric.Control({visible: false})
                    },
                    selectable: this.editable
                  }).then((shape) => {
                    shape['custom_id'] = key;
                    if ('app' in value){
                      shape['app'] = value.app;
                    }
                    shape['visible'] = true;
                    this.listAnnotation.push(shape);
                  });
                },10);
              }
            }
            else {
              setTimeout(() => {
                this.$refs.tuiImageEditor.invoke('addShape', 'rect', {
                  fill: 'transparent',
                  stroke: value.stroke,
                  strokeWidth: this.strokeWidth,
                  left: value.left,
                  top: value.top,
                  width: value.width,
                  height: value.height,
                  isRegular: false,
                  controls: {
                    ...fabric.Rect.prototype.controls,
                    mtr: new fabric.Control({visible: false})
                  },
                  selectable: this.editable
                }).then((shape) => {
                  shape['custom_id'] = key;
                  if ('app' in value){
                    shape['app'] = value.app;
                  }
                  shape['visible'] = true;
                  this.listAnnotation.push(shape);
                });
              },10);
            }
          }
        }
        if (!Array.isArray(this.image.crop)){
          for(const [key, value] of Object.entries(this.image.crop)){
            setTimeout(() => {
              this.$refs.tuiImageEditor.invoke('addShape', 'rect', {
                fill: 'transparent',
                stroke: '#1976D2',
                strokeWidth: this.strokeWidth,
                left: value.rect.left,
                top: value.rect.top,
                width: value.rect.width,
                height: value.rect.height,
                isRegular: false,
                strokeDashArray: [this.strokeDashWidth],
                controls: {
                  ...fabric.Rect.prototype.controls,
                  mtr: new fabric.Control({visible: false})
                },
                selectable: this.editable
              }).then((cropImage) => {
                cropImage['custom_id'] = key;
                cropImage['errorType'] = value['errorType'];
                cropImage['errorReason'] = value['errorReason'];
                cropImage['errorNote'] = value['errorNote'];

                this.listCropImage.push(cropImage);
                this.$refs.tuiImageEditor.invoke('discardSelection');
                this.editShape = null;

                if (key === Object.keys(this.image.crop).pop()){
                  setTimeout(() => {
                    this.isStarting = false;
                  }, 10);
                }
              });
            },10);
          }
        }
        else {
          setTimeout(() => {
            this.isStarting = false;
          }, 10);
        }

        document.getElementsByClassName('upper-canvas')[0].addEventListener('dblclick', () => {
          if (this.editShape){
            this.abnormality = [];
            this.cropPreviewImage = this.$refs.hidden_tuiImageEditor.invoke('toDataURL', {
              format: 'png',
              quality: 1,
              left: this.editShape.left - this.editShape.width/2,
              top: this.editShape.top - this.editShape.height/2,
              width: this.editShape.width,
              height: this.editShape.height
            });
            this.cropRect = this.editShape;
            this.editCropImage = this.editShape;
            const index = this.listCropImage.findIndex(item => item.id === this.editShape.id);
            this.editCropImage['custom_id'] = this.listCropImage[index].custom_id;
            this.editCropImage['errorType'] = this.listCropImage[index]['errorType'];
            this.editCropImage['errorReason'] = this.listCropImage[index]['errorReason'];
            this.editCropImage['errorNote'] = this.listCropImage[index]['errorNote'];
            this.listAnnotation.forEach((prop) => {
              if (prop.visible && (this.checkInArea(prop, this.cropRect) || this.checkInArea(prop, this.cropRect))){
                this.abnormality.push(prop);
              }
            });
            this.$refs.tuiImageEditor.invoke('discardSelection');
            this.showPreviewDialog = true;
          }
        });
      });
    },
    getImage() {
      this.image.name = this.info.name;
      this.image.src = this.info.src;
      this.strokeWidth = this.info.type === 'thermal' ? 2 : 6;
      this.strokeDashWidth = this.info.type === 'thermal' ? 20: 100,
      this.image.index = this.info.currentIndex;
      this.length = this.info.list.length;
      this.image.rotate = this.info.rotate;
      this.image.crop = this.info.crop;
      this.image.annotation_mark = this.info.annotation_mark;
      this.displayAirprobeAnnotation = !this.info.filtered;
      this.image.type = this.info.type;

      this.initCanvas();
    },
    previousImage(index) {
      if (index <= 0) {
        const image = this.info.list[0];
        const name_array = image.src.split("/");
        this.image.name = name_array[name_array.length - 1];
        this.image.src = image.src;
        this.image.index = 0;
        this.image.rotate = image.rotate;
        this.image.crop = image.crop;
        this.image.type = image.type;
        this.image.annotation_mark = image.annotation_mark;
        this.displayAirprobeAnnotation = !image.filtered;

        this.reset();
        this.initCanvas();
      } else {
        const image = this.info.list[index - 1];
        const name_array = image.src.split("/");
        this.image.name = name_array[name_array.length - 1];
        this.image.src = image.src;
        this.image.index = index - 1;
        this.image.rotate = image.rotate;
        this.image.crop = image.crop;
        this.image.type = image.type;
        this.image.annotation_mark = image.annotation_mark;
        this.displayAirprobeAnnotation = !image.filtered;

        this.reset();
        this.initCanvas();
      }
    },
    nextImage(index) {
      if (index >= this.info.list.length - 1) {
        const image = this.info.list[this.info.list.length - 1];
        const name_array = image.src.split("/");
        this.image.name = name_array[name_array.length - 1];
        this.image.src = image.src;
        this.image.index = this.info.list.length - 1;
        this.image.rotate = image.rotate;
        this.image.crop = image.crop;
        this.image.type = image.type;
        this.image.annotation_mark = image.annotation_mark;
        this.displayAirprobeAnnotation = !image.filtered;

        this.reset();
        this.initCanvas();
      } else {
        const image = this.info.list[index + 1];
        const name_array = image.src.split("/");
        this.image.name = name_array[name_array.length - 1];
        this.image.src = image.src;
        this.image.index = index + 1;
        this.image.rotate = image.rotate;
        this.image.crop = image.crop;
        this.image.type = image.type;
        this.image.annotation_mark = image.annotation_mark;
        this.displayAirprobeAnnotation = !image.filtered;

        this.reset();
        this.initCanvas();
      }
    },
    setZoomerWidth() {
      const img = new Image();
      img.src = this.image.src;
      img.onload = () => {
        const element = document.getElementById("zoomer");
        const height = element.offsetHeight;
        let calc_width = (img.width * height) / img.height;
        if (calc_width > window.innerWidth) {
          calc_width = window.innerWidth;
        }
        element.style.width = calc_width + "px";
      };
    },
    rotateImage() {
      this.isCrop = false;
      this.isMarkAnnotation = false;
      this.$refs.tuiImageEditor.invoke('stopDrawingMode');
      this.rotateAngle = (this.rotateAngle + 90) % 360;
      this.$refs.hidden_tuiImageEditor.invoke('rotate', 90, true);
      this.$refs.tuiImageEditor.invoke('rotate', 90, true).then(() => {
        this.resetCanvasSize();
        this.listCropImage.forEach(item => {
          const object = this.$refs.tuiImageEditor.invoke('getObjectProperties', item.id, ['left', 'top', 'width', 'height']);
          item.left = object.left;
          item.top = object.top;
          const width = item.width;
          const height = item.height;
          item.width = height;
          item.height = width;
          item.rect = {left: item.left, top: item.top, width: item.width, height: item.height}
        })
        this.listAnnotation.forEach(item => {
          const object = this.$refs.tuiImageEditor.invoke('getObjectProperties', item.id, ['left', 'top', 'width', 'height']);
          item.left = object.left;
          item.top = object.top;
          const width = item.width;
          const height = item.height;
          item.width = height;
          item.height = width;
        });
        this.$emit('updateRotate', this.image.src, this.rotateAngle, this.info.type, this.info.app, this.listCropImage, this.listAnnotation);
        this.axios.post('/api/fly-route-coordinate/image/rotate', {fileName: this.image.name.split('.')[0], folderId: this.folderId, colorType: this.info.type, rotate: this.rotateAngle, app: this.info.app, crop_list: this.listCropImage, annotation_list: this.listAnnotation});
        if (this.listCropImage.length > 0){
          this.axios.put('/api/report/update-image/' + this.folderId, {fileName: this.image.name.split('.')[0]});
        }
      });
    },
    close() {
      store.updateOpenDialog(false);
      document.removeEventListener('keydown', this.deleteKeyPressed);
      this.$emit("closed");
      this.$emit("input");
    },
    startCrop(){
      this.isCrop = !this.isCrop;
      this.isMarkAnnotation = false;
      this.$refs.tuiImageEditor.invoke('stopDrawingMode');
      if (this.isCrop){
        this.$refs.tuiImageEditor.invoke('startDrawingMode', 'CROPPER');
      }
      else {
        this.$refs.tuiImageEditor.invoke('stopDrawingMode');
      }
    },
    mouseDown(evt){
      this.isStartCrop = true;
      this.abnormality = [];
      this.cropRect = null;
      this.mouseDownPosition = {x: evt.offsetX, y: evt.offsetY};
    },
    checkInArea(point, area){
      const point_tl = {top: point.top - point.height/2, left: point.left - point.width/2};
      const point_tr = {top: point.top - point.height/2, left: point.left + point.width/2};
      const point_bl = {top: point.top + point.height/2, left: point.left - point.width/2};
      const point_br = {top: point.top + point.height/2, left: point.left + point.width/2};
      const area_tl = {top: area.top - area.height/2, left: area.left - area.width/2};
      const area_br = {top: area.top + area.height/2, left: area.left + area.width/2};

      return (point_tl.top < area_br.top && point_tl.left < area_br.left && point_tl.top > area_tl.top && point_tl.left > area_tl.left) ||
          (point_tr.top < area_br.top && point_tr.left < area_br.left && point_tr.top > area_tl.top && point_tr.left > area_tl.left) ||
          (point_bl.top < area_br.top && point_bl.left < area_br.left && point_bl.top > area_tl.top && point_bl.left > area_tl.left) ||
          (point_br.top < area_br.top && point_br.left < area_br.left && point_br.top > area_tl.top && point_br.left > area_tl.left)
    },
    mouseUp(evt){
      evt.preventDefault();
      if (this.isCrop){
        const cropRect = this.$refs.tuiImageEditor.invoke('getCropzoneRect');
        if (this.isStartCrop && cropRect && (cropRect.left != 0 || cropRect.top != 0)){
          cropRect.top += cropRect.height/2;
          cropRect.left += cropRect.width/2;
          this.cropPreviewImage = this.$refs.hidden_tuiImageEditor.invoke('toDataURL', {
            format: 'png',
            quality: 1,
            left: cropRect.left - cropRect.width/2,
            top: cropRect.top - cropRect.height/2,
            width: cropRect.width,
            height: cropRect.height
          });
          this.cropRect = cropRect;
          this.listAnnotation.forEach((prop) => {
            if (prop.visible && (this.checkInArea(prop, this.cropRect) || this.checkInArea(prop, this.cropRect))){
              this.abnormality.push(prop);
            }
          });
          if (this.editShape){
            this.editCropImage = this.listCropImage.filter(item => {
              return item.id === this.editShape.id;
            });
          }
          else {
            this.editCropImage = null;
          }
          this.showPreviewDialog = true;
        }
      }
      this.isStartCrop = false;
      if (this.editShape && evt.offsetX !== this.mouseDownPosition.x && evt.offsetY !== this.mouseDownPosition.y){
        this.cropPreviewImage = this.$refs.hidden_tuiImageEditor.invoke('toDataURL', {
          format: 'png',
          quality: 1,
          left: this.editShape.left - this.editShape.width/2,
          top: this.editShape.top - this.editShape.height/2,
          width: this.editShape.width,
          height: this.editShape.height
        });
        this.editCropImage = this.editShape;
        this.cropRect = this.editShape;
        this.listAnnotation.forEach((prop) => {
          if (prop.visible && this.checkInArea(prop, this.cropRect) || this.checkInArea(prop, this.cropRect)){
            this.abnormality.push(prop);
          }
        });
        this.showPreviewDialog = true;
      }
      if (this.editAnnotationShape && evt.offsetX !== this.mouseDownPosition.x && evt.offsetY !== this.mouseDownPosition.y){
        this.axios
            .post('/api/report/add-annotation/' + this.folderId, {filename: this.image.name.split('.')[0], abnormality_shape: this.editAnnotationShape, annotation: this.editAnnotationShape['custom_id']})
            .then(() => {
              const index = this.listAnnotation.findIndex(item => item.custom_id === this.editAnnotationShape['custom_id']);
              this.listAnnotation[index] = this.editAnnotationShape;
            }).finally(() => {
              this.editAnnotationShape = null;
            });
      }
    },
    draw(status, data, addedAbnormalities, modifiedAbnormalities, deletedAbnormalities){
      if (status){
        let cropRect;
        if (this.editCropImage){
          cropRect = this.editCropImage;
          this.$refs.tuiImageEditor.invoke('setObjectPosition', this.editCropImage.id, {
            x: this.editCropImage.left,
            y: this.editCropImage.top,
            originX: 'center',
            originY: 'center'
          });
          this.editCropImage['custom_id'] = data.custom_id;
          this.editCropImage['errorType'] = data.errorType;
          this.editCropImage['errorReason'] = data.errorReason;
          this.editCropImage['errorNote'] = data.errorNote;
          this.$emit('updateCropImage', this.image.src, this.editCropImage, this.info.type, this.info.app);
          this.listCropImage[this.listCropImage.findIndex(item => item.id === this.editCropImage.id)] = this.editCropImage;
        }
        else {
          cropRect = this.$refs.tuiImageEditor.invoke('getCropzoneRect');
          this.$refs.tuiImageEditor.invoke('addShape', 'rect' , {
            fill: 'transparent',
            stroke: '#1976D2',
            strokeWidth: this.strokeWidth,
            left: cropRect.left + cropRect.width/2,
            top: cropRect.top + cropRect.height/2,
            width: cropRect.width,
            height: cropRect.height,
            isRegular: false,
            strokeDashArray: [this.strokeDashWidth],
            controls: {
              ...fabric.Rect.prototype.controls,
              mtr: new fabric.Control({visible: false})
            },
          }).then((cropImage) => {
            cropImage['custom_id'] = data.custom_id;
            cropImage['errorType'] = data.errorType;
            cropImage['errorReason'] = data.errorReason;
            cropImage['errorNote'] = data.errorNote;
            this.listCropImage.push(cropImage);
            this.$emit('addCropImage', this.image.src, cropImage, this.info.type, this.info.app);
          });
          this.isStartCrop = false;
        }
        addedAbnormalities.forEach(item => {
          item.top = item.top + this.cropRect.top - this.cropRect.height/2;
          item.left = item.left + this.cropRect.left - this.cropRect.width/2;
          this.axios
              .post('/api/report/add-annotation/' + this.folderId, {abnormality_shape: item, filename: this.image.name})
              .then((response) => {
                this.$refs.tuiImageEditor.invoke('addShape', 'rect', {
                  fill: 'transparent',
                  stroke: item.stroke,
                  strokeWidth: this.strokeWidth,
                  left: item.left,
                  top: item.top,
                  width: item.width,
                  height: item.height,
                  isRegular: false,
                  controls: {
                    ...fabric.Rect.prototype.controls,
                    mtr: new fabric.Control({visible: false})
                  },
                }).then((shape) => {
                  shape['custom_id'] = response.data;
                  shape['visible'] = true;
                  this.listAnnotation.push(shape);
                  this.$emit('addCropAbnormality', this.image.src, shape, this.info.type, this.info.app);
                  this.$refs.tuiImageEditor.invoke('discardSelection');
                });
              });
        });
        modifiedAbnormalities.forEach(item => {
          item.top = item.top + this.cropRect.top - this.cropRect.height/2;
          item.left = item.left + this.cropRect.left - this.cropRect.width/2;
          const i = this.listAnnotation.findIndex(im => im.custom_id === item.custom_id);
          if (i >= 0){
            this.listAnnotation[i].top = item.top;
            this.listAnnotation[i].left = item.left;
            this.listAnnotation[i].height = item.height;
            this.listAnnotation[i].width = item.width;
            if (item.modified === 'scaled'){
              this.listAnnotation[i].top = item.top + item.height/2;
              this.listAnnotation[i].left = item.left + item.width/2;
            }
            this.$emit('updateCropAbnormality', this.image.src, this.listAnnotation[i], this.info.type, this.info.app);
            this.axios.post('/api/report/add-annotation/' + this.folderId, {abnormality_shape: this.listAnnotation[i], filename: this.image.name, annotation: this.listAnnotation[i].custom_id});
            this.$refs.tuiImageEditor.invoke('setObjectPropertiesQuietly', this.listAnnotation[i].id, {
              left: this.listAnnotation[i].left,
              top: this.listAnnotation[i].top,
              width: this.listAnnotation[i].width,
              height: this.listAnnotation[i].height
            });
          }
        });
        deletedAbnormalities.forEach(item => {
          const i = this.listAnnotation.findIndex(it => it.custom_id === item.custom_id);
          if (i >= 0){
            this.$emit('deleteCropAbnormality', this.image.src, this.listAnnotation[i], this.info.type, this.info.app);
            const id = this.listAnnotation[i].id;
            this.listAnnotation.splice(i, 1);
            this.axios
                .delete('/api/report/annotation/delete/' + this.folderId, {data: {custom_id: item.custom_id, filename: this.image.name.split('.')[0]}});
            setTimeout(() => {
              this.$refs.tuiImageEditor.invoke('removeObject', id);
            }, 5);
          }
        });
      }
      else {
        if (this.editShape && this.editShape.stroke === '#1976D2'){
          const index = this.listCropImage.findIndex(item => item.id === this.editShape.id);
          this.$refs.tuiImageEditor.invoke('setObjectPosition', this.editShape.id, {
            x: this.listCropImage[index].left,
            y: this.listCropImage[index].top,
            originX: 'center',
            originY: 'center'
          });
        }
      }
      if (this.isCrop){
        this.$refs.tuiImageEditor.invoke('setCropzoneRect', 0);
      }
      this.$refs.tuiImageEditor.invoke('discardSelection');
      this.editShape = null;
      this.editCropImage = null;
    },
    zoom(event){
      event.preventDefault();
      this.pointerPosX = event.offsetX;
      this.pointerPosY = event.offsetY;
      if (event.detail) event.wheelDelta = event.detail * -10;
      let currTime = Date.now();
      if (Math.abs(event.wheelDelta) === 120) {
        // Throttle the TouchPad pinch on Mac, or it will be too sensitive
        if (currTime - this.lastFullWheelTime > 50) {
          this.onMouseWheelDo(event.wheelDelta);
          this.lastFullWheelTime = currTime;
        }
      } else {
        if (currTime - this.lastWheelTime > 50 && typeof event.deltaX === 'number') {
          this.lastWheelDirection = (event.detail == 0 && Math.abs(event.deltaX) > Math.abs(event.deltaY)) ? 'x' : 'y';
        }
        if (this.lastWheelDirection === 'y') {
          this.onMouseWheelDo(event.wheelDelta);
        }
      }
      this.lastWheelTime = currTime;
    },
    onMouseWheelDo (wheelDelta) {
      // Value basis: One mouse wheel (wheelDelta=+-120) means 1.25/0.8 scale.
      let scaleDelta = Math.pow(1.25, wheelDelta / 120);
      this.tryToScale(scaleDelta);
    },
    tryToScale (scaleDelta) {
      let newScale = this.scale * scaleDelta;
      if (this.zoomingElastic) {
        // damping
        if (newScale < this.minScale || newScale > this.maxScale) {
          let log = Math.log2(scaleDelta);
          log *= 0.2;
          scaleDelta = Math.pow(2, log);
          newScale = this.scale * scaleDelta;
        }
      } else {
        if (newScale < this.minScale) newScale = this.minScale;
        else if (newScale > this.maxScale) newScale = this.maxScale;
      }
      scaleDelta = newScale / this.scale;
      const canvas = document.getElementsByClassName('upper-canvas')[0];
      if (newScale > this.scale){
        this.currentCenter.x = this.pointerPosX/canvas.clientWidth * canvas.width;
        this.currentCenter.y = this.pointerPosY/canvas.clientHeight * canvas.height;
      }
      this.scale = newScale;
      this.$refs.tuiImageEditor.invoke('zoom', {x: this.currentCenter.x, y: this.currentCenter.y, zoomLevel: this.scale});
    },
    markAnnotation(){
      this.isMarkAnnotation = !this.isMarkAnnotation;
      this.isCrop = false;
      this.$refs.tuiImageEditor.invoke('stopDrawingMode');
      if (this.isMarkAnnotation){
        this.$refs.tuiImageEditor.invoke('startDrawingMode', 'SHAPE');
        this.$refs.tuiImageEditor.invoke('setDrawingShape', 'rect', {
          fill: 'transparent',
          stroke: '#FF5252',
          strokeWidth: this.strokeWidth,
          controls: {
            ...fabric.Rect.prototype.controls,
            mtr: new fabric.Control({visible: false})
          },
        });
      }
    },
    objectAdded(props){
      this.$refs.tuiImageEditor.invoke('setObjectProperties', props.id, {
        controls: {
          ...fabric.Rect.prototype.controls,
          mtr: new fabric.Control({visible: false})
        }
      }).then(() => {
        if (props.type === 'rect' && props.stroke === '#FF5252'){
          this.axios
              .post('/api/report/add-annotation/' + this.folderId, {abnormality_shape: props, filename: this.image.name})
              .then((response) => {
                props['custom_id'] = response.data;
                props['visible'] = true;
                this.listAnnotation.push(props);
                this.$emit('addCropAbnormality', this.image.src, props, this.info.type, this.info.app);
              });
          this.$refs.tuiImageEditor.invoke('discardSelection');
        }
      });
    },
    objectActivated(props){
      if (props.type === 'rect' && props.stroke === '#1976D2') {
        this.editShape = props;
      }
      if (props.type === 'rect' && props.stroke === '#FF5252') {
        if (this.isStarting){
          this.$refs.tuiImageEditor.invoke('discardSelection');
        }
        else {
          if (props.width === 20 && props.height === 20){
            setTimeout(() => {
              this.$refs.tuiImageEditor.invoke('removeObject', props.id);
            }, 0);
          }
          else {
            const index = this.listAnnotation.findIndex(item => item.id === props.id);
            if (index >= 0){
              this.listAnnotation[index].left = props.left;
              this.listAnnotation[index].top = props.top;
              this.listAnnotation[index].width = props.width;
              this.listAnnotation[index].height = props.height;
              this.editAnnotationShape = this.listAnnotation[index];
            }
          }
        }
      }
    },
    objectMoved(props){
      if (props.type === 'rect' && props.stroke === '#1976D2') {
        this.editShape = props;
        const index = this.listCropImage.findIndex(item => item.id === props.id);
        this.editShape['custom_id'] = this.listCropImage[index].custom_id;
        this.editShape['errorType'] = this.listCropImage[index]['errorType'];
        this.editShape['errorReason'] = this.listCropImage[index]['errorReason'];
        this.editShape['errorNote'] = this.listCropImage[index]['errorNote'];
      }
      if (props.type === 'rect' && props.stroke === '#FF5252'){
        const index = this.listAnnotation.findIndex(item => item.id === props.id);
        this.listAnnotation[index].left = props.left;
        this.listAnnotation[index].top = props.top;
        this.listAnnotation[index].width = props.width;
        this.listAnnotation[index].height = props.height;
        this.$emit('updateCropAbnormality', this.image.src, this.listAnnotation[index], this.info.type, this.info.app);
        this.editAnnotationShape = this.listAnnotation[index];
      }
    },
    objectScaled(props){
      const oCoords = this.$refs.tuiImageEditor.invoke('getObjectProperties', props.id, ['oCoords']);
      props.left = oCoords.oCoords.tl.x + props.width/2;
      props.top = oCoords.oCoords.tl.y + props.height/2;
      if (props.type === 'rect' && props.stroke === '#FF5252'){
        const index = this.listAnnotation.findIndex(item => item.id === props.id);
        this.listAnnotation[index].left = props.left;
        this.listAnnotation[index].top = props.top;
        this.listAnnotation[index].width = props.width;
        this.listAnnotation[index].height = props.height;
        this.$emit('updateCropAbnormality', this.image.src, this.listAnnotation[index], this.info.type, this.info.app);
        this.editAnnotationShape = this.listAnnotation[index];
      }
      if (props.type === 'rect' && props.stroke === '#1976D2'){
        this.editShape = props;
        const index = this.listCropImage.findIndex(item => item.id === props.id);
        this.editShape['custom_id'] = this.listCropImage[index].custom_id;
        this.editShape['errorType'] = this.listCropImage[index]['errorType'];
        this.editShape['errorReason'] = this.listCropImage[index]['errorReason'];
        this.editShape['errorNote'] = this.listCropImage[index]['errorNote'];
      }
    },
    deleteKeyPressed(evt){
      if (evt.key === "Backspace" || evt.key === "Delete") {
        if (!this.showPreviewDialog && this.editable){
          if (this.editAnnotationShape && (this.editAnnotationShape.width !== 20 || this.editAnnotationShape.height !== 20)){
            const index = this.listAnnotation.findIndex(item => item.id === this.editAnnotationShape.id);
            const custom_id = this.listAnnotation[index].custom_id;
            this.$emit('deleteCropAbnormality', this.image.src, this.listAnnotation[index], this.info.type, this.info.app);
            this.listAnnotation.splice(index, 1);
            this.editAnnotationShape = null;
            this.axios
                .delete('/api/report/annotation/delete/' + this.folderId, {data: {custom_id: custom_id, filename: this.image.name.split('.')[0]}});
          }
          if (this.editShape){
            if (this.editShape.stroke === '#1976D2'){
              const index = this.listCropImage.findIndex(item => item.id === this.editShape.id);
              const custom_id = this.listCropImage[index].custom_id;
              this.$emit('deleteCropImage', this.image.src, this.listCropImage[index], this.info.type, this.info.app);
              this.listCropImage.splice(index, 1);
              this.editShape = null;
              this.axios
                  .delete('/api/report/crop-image/delete/' + this.folderId, {data: {custom_id: custom_id, filename: this.image.name.split('.')[0]}});
            }
            else if (this.editShape.stroke === '#FF5252'){
              const index = this.listAnnotation.findIndex(item => item.id === this.editShape.id);
              const custom_id = this.listAnnotation[index].custom_id;
              this.$emit('deleteCropAbnormality', this.image.src, this.listAnnotation[index], this.info.type, this.info.app);
              this.listAnnotation.splice(index, 1);
              this.editShape = null;
              this.axios
                  .delete('/api/report/annotation/delete/' + this.folderId, {data: {custom_id: custom_id, filename: this.image.name.split('.')[0]}});
            }
          }
        }
      }
    },
    turnoff(){
      this.$refs.tuiImageEditor.invoke('discardSelection');
    },
    downloadImage(url){
      download(url.concat('?timestamp=1'));
    },
    showAirprobeAnnotation(){
      this.axios.put('/api/report/annotation/filter/' + this.folderId, {'image': this.image.name, 'filtered': !this.displayAirprobeAnnotation});
      this.$emit('showAirprobeAnnotation', this.image.src, !this.displayAirprobeAnnotation, this.image.type, this.info.app);
      const airprobeAIAnnotation = [];
      const notAirprobeAIAnnotation = [];
      if (!this.displayAirprobeAnnotation){
        this.listAnnotation.forEach((item) => {
          if ('app' in item){
            if (item.app === 'airprobe'){
              airprobeAIAnnotation.push(item);
              item.visible = false;
            }
            else {
              notAirprobeAIAnnotation.push(item);
              item.visible = true;
            }
          }
          else {
            notAirprobeAIAnnotation.push(item);
            item.visible = true;
          }
        })
        this.$refs.tuiImageEditor.invoke('clearObjects').then(() => {
          notAirprobeAIAnnotation.forEach(value => {
            setTimeout(() => {
              this.$refs.tuiImageEditor.invoke('addShape', 'rect', {
                fill: 'transparent',
                stroke: value.stroke,
                strokeWidth: this.strokeWidth,
                left: value.left,
                top: value.top,
                width: value.width,
                height: value.height,
                isRegular: false,
                controls: {
                  ...fabric.Rect.prototype.controls,
                  mtr: new fabric.Control({visible: false})
                },
              }).then(() => {
                this.$refs.tuiImageEditor.invoke('discardSelection');
              });
            }, 10);
          })
        })
      }
      else {
        this.listAnnotation.forEach((item) => {
          if ('app' in item){
            item.visible = true;
          }
        })
        this.$refs.tuiImageEditor.invoke('clearObjects').then(() => {
          this.listAnnotation.forEach(value => {
            setTimeout(() => {
              this.$refs.tuiImageEditor.invoke('addShape', 'rect', {
                fill: 'transparent',
                stroke: value.stroke,
                strokeWidth: this.strokeWidth,
                left: value.left,
                top: value.top,
                width: value.width,
                height: value.height,
                isRegular: false,
                controls: {
                  ...fabric.Rect.prototype.controls,
                  mtr: new fabric.Control({visible: false})
                },
              }).then(() => {
                this.$refs.tuiImageEditor.invoke('discardSelection');
              });
            }, 10);
          })
        })
      }
      for(const [key, value] of Object.entries(this.image.crop)){
        setTimeout(() => {
          this.$refs.tuiImageEditor.invoke('addShape', 'rect', {
            fill: 'transparent',
            stroke: '#1976D2',
            strokeWidth: this.strokeWidth,
            left: value.rect.left,
            top: value.rect.top,
            width: value.rect.width,
            height: value.rect.height,
            isRegular: false,
            strokeDashArray: [this.strokeDashWidth],
            controls: {
              ...fabric.Rect.prototype.controls,
              mtr: new fabric.Control({visible: false})
            },
          }).then((cropImage) => {
            cropImage['custom_id'] = key;
            cropImage['errorType'] = value['errorType'];
            cropImage['errorReason'] = value['errorReason'];
            cropImage['errorNote'] = value['errorNote'];

            this.listCropImage.push(cropImage);
            this.$refs.tuiImageEditor.invoke('discardSelection');
            this.editShape = null;

            if (key === Object.keys(this.image.crop).pop()){
              setTimeout(() => {
                this.isStarting = false;
              }, 10);
            }
          });
        },10);
      }
    },
    redo(length){
      if (length != 0){
        this.$refs.tuiImageEditor.invoke('clearRedoStack');
      }
    },
    undo(length){
      if (length != 0){
        this.$refs.tuiImageEditor.invoke('clearUndoStack');
      }
    }
  },

  mounted() {
    store.updateOpenDialog(true);
    this.getImage();
    document.addEventListener('keydown', this.deleteKeyPressed);
  },

  computed: {
    editable() {
      return store.state.currentReportStatus === 'NEW' || store.state.currentReportStatus === 'BRANCH_REQUEST_REJECTED' || store.state.currentReportStatus === 'AREA_REQUEST_REJECTED' || store.state.currentReportStatus === 'SUBMIT_REJECTED'
    }
  }
};
</script>
<style lang="scss">
.title {
  border-radius: 4px;
  width: auto;
  padding-bottom: 0;
  padding-top: 0;
}
#zoomer {
  display: block;
  margin-left: auto;
  margin-right: auto;
  height: calc(100vh - 70px);
}
.zoomer-img {
  object-fit: contain;
  width: 100%;
  height: 100%;
}
.btn-close {
  position: absolute;
  right: 25px;
  top: 15px;
}
.show-image {
    margin-right: 0px !important;
    margin-left: 0px !important;
}
</style>

